import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Select from 'react-select'
import { SYSTEM_OPTIONS, GOAL_OPTIONS, SLIDES, CARAT_OPTIONS, RANGE_OPTIONS, FINDER_SELECT_STYLE } from './list';

/** SYSTEM/GOAL lists are [value, label] pairs whose first entry ('') is the
 * placeholder — drop it and let react-select render the placeholder itself. */
const pairsToOptions = (pairs) => pairs.filter(([v]) => v !== '').map(([value, label]) => ({ value, label }))
/** CARAT/RANGE lists are already {value,label}, with a ''-valued placeholder row. */
const rowsToOptions = (rows) => rows.filter(r => r.value !== '')



export default function FinderSliderSection() {
  const navigate = useNavigate();

  const [mode, setMode] = useState('system')
  const [slide, setSlide] = useState(0)

  // Finder selections. The item list depends on the mode, so a mode
  // switch clears the picked item (its options no longer exist).
  const [item, setItem] = useState(null)
  const [carat, setCarat] = useState(null)
  const [range, setRange] = useState(null)

  const switchMode = (m) => {
    if (m === mode) return
    setMode(m)
    setItem(null)
  }

  const itemPairs = mode === 'system' ? SYSTEM_OPTIONS : GOAL_OPTIONS
  const itemOptions = pairsToOptions(itemPairs)
  const itemPlaceholder = itemPairs[0][1]


  // The selects are controlled, so the picked values live in state —
  // the submit event itself carries no form data worth reading.
  function handleSubmit(e) {
    e.preventDefault();
    const selection = {
      mode,
      s: item?.value,
      carat: carat?.value,
      range: range?.value,
    }
    // Only picked filters become query params — empty keys stay out of the URL.
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(selection)) {
      if (value) params.set(key, value)
    }
    const qs = params.toString()
    // Page transition (home → products) — must PUSH so back returns here.
    // `replace` would overwrite the home entry and back would skip it;
    // replace is only for in-page filter updates on the products page itself.
    navigate(`/products${qs ? `?${qs}` : ''}`)
  }

  return (
    <section className="py-12 md:px-16 bg-linear-to-b from-gray-50 to-white">
      <div className="max-w-350 mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
          {/* FINDER CARD */}
          <div className="lg:col-span-3 bg-linear-to-b from-[#1C060D] via-[#2B0B14] to-[#3B0A12] rounded-3xl shadow-2xl border border-white/10 flex flex-col justify-between relative overflow-hidden group select-none transition-all duration-500 hover:shadow-[0_20px_50px_rgba(242,160,7,0.15)]">
            <div className="relative h-full min-h-90 overflow-hidden bg-linear-to-b from-[#2B0B14] to-primary rounded-3xl p-7 md:p-8 border border-white/10 shadow-xl shadow-red-950/20 flex flex-col justify-center font-normal uppercase tracking-tight select-none">
              <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
                <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-[radial-linear(circle,rgba(232,119,15,0.35),transparent_70%)] blur-2xl animate-[pulse_4s_ease-in-out_infinite]"></div>
                <div className="absolute -left-16 bottom-0 h-44 w-44 rounded-full bg-[radial-linear(circle,rgba(230,200,120,0.18),transparent_70%)] blur-2xl animate-[pulse_6s_ease-in-out_infinite_reverse]"></div>
                <svg viewBox="0 0 120 130" className="absolute -right-6 top-6 w-44 h-44 opacity-[0.16] animate-[spin_80s_linear_infinite]" fill="none" stroke="#E6C878" strokeWidth="1.4">
                  <path d="M60 6 L102 38 L84 122 L36 122 L18 38 Z"></path><path d="M18 38 L102 38"></path><path d="M60 6 L43 38 L60 122 L77 38 Z"></path><path d="M43 38 L36 122 M77 38 L84 122"></path>
                </svg>
              </div>

              <form onSubmit={handleSubmit} className="relative z-10 space-y-7">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 mb-1">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 text-secondary animate-pulse"><path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"></path></svg>
                    <span className="text-[9px] font-mono font-normal text-secondary uppercase tracking-[0.4em]">REMEDY PROTOCOL</span>
                  </div>
                  <h2 className="text-xl md:text-2xl font-heading text-[#FBF3E4] leading-tight font-normal uppercase tracking-wide">
                    {mode === 'system' ? 'GEMSTONE FINDER' : 'REMEDY FINDER'}
                  </h2>
                  <p className="text-[8px] font-mono font-normal text-white/30 uppercase tracking-[0.2em]">SYSTEM.DISCOVERY.V4.2</p>
                </div>

                <div className="flex items-center gap-6">
                  <button type="button" onClick={() => switchMode('system')} className="flex items-center gap-3 group outline-none">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${mode === 'system' ? 'border-secondary' : 'border-white/20'}`}>
                      {mode === 'system' && <div className="w-2 h-2 rounded-full bg-secondary"></div>}
                    </div>
                    <span className={`text-[9px] font-normal uppercase tracking-widest transition-colors ${mode === 'system' ? 'text-secondary' : 'text-white/40'}`}>BY SYSTEM ITEM</span>
                  </button>
                  <button type="button" onClick={() => switchMode('spiritual')} className="flex items-center gap-3 group outline-none">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${mode === 'spiritual' ? 'border-secondary' : 'border-white/20'}`}>
                      {mode === 'spiritual' && <div className="w-2 h-2 rounded-full bg-secondary"></div>}
                    </div>
                    <span className={`text-[9px] font-normal uppercase tracking-widest transition-colors ${mode === 'spiritual' ? 'text-secondary' : 'text-white/40 group-hover:text-white'}`}>BY SPIRITUAL GOAL</span>
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-4 normal-case">
                  <Select
                    styles={FINDER_SELECT_STYLE}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    options={itemOptions}
                    value={item}
                    onChange={setItem}
                    placeholder={itemPlaceholder}
                    aria-label={itemPlaceholder}
                    isClearable
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <Select
                      styles={FINDER_SELECT_STYLE}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      options={rowsToOptions(CARAT_OPTIONS)}
                      value={carat}
                      onChange={setCarat}
                      placeholder={CARAT_OPTIONS[0].label}
                      aria-label={CARAT_OPTIONS[0].label}
                      isClearable
                    />
                    <Select
                      styles={FINDER_SELECT_STYLE}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      options={rowsToOptions(RANGE_OPTIONS)}
                      value={range}
                      onChange={setRange}
                      placeholder={RANGE_OPTIONS[0].label}
                      aria-label={RANGE_OPTIONS[0].label}
                      isClearable
                    />
                  </div>
                </div>

                <div className="space-y-4 pt-2">
                  <button className="inline-flex items-center justify-center whitespace-nowrap active:scale-95 px-6 py-2 w-full h-14 rounded-xl bg-secondary text-white hover:bg-white hover:text-[#2B0B14] transition-all duration-500 font-semibold uppercase tracking-[0.2em] text-[10px] group shadow-lg shadow-black/30 border-none">
                    BEGIN SEARCH PROTOCOL
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 ml-2 transition-transform group-hover:scale-110"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></svg>
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* SLIDER */}
          <div className="lg:col-span-7">
            <div className="group/slider relative h-full min-h-90 overflow-hidden rounded-3xl border border-gray-200/50 shadow-xl shadow-primary/5 bg-white">
              {/* The active slide sits in flow so the card grows to fit it — when
                  every slide was absolute the content overflowed the fixed
                  min-height and the CTA was clipped. Inactive slides stay
                  absolute so the cross-fade still overlays. */}
              {SLIDES.map((s, i) => (
                <div key={i} className={`transition-opacity duration-500 ${slide === i ? 'relative opacity-100' : 'absolute inset-0 opacity-0 pointer-events-none'}`}>
                  <div className={`absolute inset-0 bg-linear-to-br ${s.from} ${s.via} ${s.to}`}></div>
                  <div className="absolute inset-0 opacity-[0.5] mix-blend-soft-light bg-[radial-linear(circle_at_top_right,white,transparent_60%)]"></div>
                  <div className="relative h-full flex items-center">
                    <div className="flex-1 min-w-0 px-6 sm:px-8 md:px-12 py-8 md:py-10 pb-14 md:pb-12 max-w-full sm:max-w-[62%] space-y-4 md:space-y-5">
                      <span className="inline-flex items-center gap-2 text-[9px] tracking-[0.35em] font-medium text-primary/60"><i className="fa-solid fa-star w-3 h-3 text-secondary"></i> {s.tag}</span>
                      <h3 className="font-heading leading-[0.98] tracking-tight">
                        <span className="block text-2xl md:text-3xl xl:text-[2.5rem] text-foreground/90">{s.title1}</span>
                        <span className="block text-2xl md:text-3xl xl:text-[2.5rem] text-primary">{s.title2}</span>
                      </h3>
                      <p className="text-[13px] md:text-sm text-foreground/60 leading-relaxed font-body normal-case max-w-md">{s.desc}</p>
                      <div className="flex flex-wrap items-center gap-3 pt-1">
                        <span className="text-xs md:text-sm font-semibold tracking-wide text-foreground/80">{s.label}</span>
                        <span className="inline-flex items-center rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-white shadow-md">{s.price}</span>
                      </div>
                      <div className="pt-2">
                        <Link
                          to={s.cat_slug}
                          className="group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3 text-[11px] font-semibold tracking-[0.2em] text-white shadow-lg shadow-primary/20 transition-all hover:bg-secondary"
                        >
                          ORDER NOW <i className="fa-solid fa-arrow-right w-4 h-4 transition-transform group-hover:translate-x-1"></i>
                        </Link>
                      </div>
                    </div>
                    <div className="hidden sm:flex relative flex-1 min-w-0 h-full items-center justify-center overflow-hidden">
                      <div className="absolute inset-0 z-0"><div className="stars-twinkle"></div></div>
                      <div className="absolute h-64 w-64 rounded-full blur-3xl bg-[#e0b84e]/40 z-0"></div>
                      <img src={s.img} className="relative h-40 w-40 md:h-56 md:w-56 xl:h-64 xl:w-64 max-w-full drop-shadow-2xl z-10" alt={s.label} />
                    </div>
                  </div>
                </div>
              ))}

              <button onClick={() => setSlide((slide + SLIDES.length - 1) % SLIDES.length)} className="absolute left-3 top-1/2 -translate-y-1/2 z-20 grid h-9 w-9 place-items-center rounded-full bg-white/70 text-primary shadow-md backdrop-blur-md opacity-100 lg:opacity-0 lg:group-hover/slider:opacity-100 transition-all hover:bg-white"><i className="fa-solid fa-chevron-left"></i></button>
              <button onClick={() => setSlide((slide + 1) % SLIDES.length)} className="absolute right-3 top-1/2 -translate-y-1/2 z-20 grid h-9 w-9 place-items-center rounded-full bg-white/70 text-primary shadow-md backdrop-blur-md opacity-100 lg:opacity-0 lg:group-hover/slider:opacity-100 transition-all hover:bg-white"><i className="fa-solid fa-chevron-right"></i></button>

              <div className="absolute bottom-5 right-8 md:right-12 z-20 flex items-center gap-2">
                {SLIDES.map((_, i) => (
                  <button key={i} onClick={() => setSlide(i)} className={`h-1.5 rounded-full transition-all ${slide === i ? 'w-7 bg-primary' : 'w-1.5 bg-primary/30 hover:bg-primary/50'}`}></button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
